import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests; let FormData set its own Content-Type (with boundary)
api.interceptors.request.use((config) => {
  if (!config.headers) config.headers = {};
  const token = localStorage.getItem('nepwed_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  if (config.data instanceof FormData) {
    // Delete case-insensitively — axios can normalize to either form
    Object.keys(config.headers).forEach(k => {
      if (k.toLowerCase() === 'content-type') delete config.headers[k];
    });
  }
  return config;
});

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const verifyEmail = (token) => api.get(`/auth/verify-email/${token}`);
export const resendVerification = (email) => api.post('/auth/resend-verification', { email });
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });
export const removeHelperRole = () => api.post('/auth/remove-helper-role');
export const switchRole = (role) => api.post('/auth/switch-role', { role });
export const upgradeToCoupleRole = (data) => api.post('/auth/upgrade-to-couple', data);

// Vendors
export const getVendors = (params) => api.get('/vendors', { params });
export const getVendorCities = () => api.get('/vendors/cities');
export const getVendor = (slug) => api.get(`/vendors/${slug}`);
export const inquireVendor = (id, data) => api.post(`/vendors/${id}/inquire`, data);

// Categories
export const getCategories = () => api.get('/categories');

// Weddings
export const getWeddings = () => api.get('/weddings');
export const getWedding = (id) => api.get(`/weddings/${id}`);
export const createWedding = (data) => api.post('/weddings', data);
export const updateWedding = (id, data) => api.put(`/weddings/${id}`, data);

// Checklist
export const getChecklist = (weddingId) => api.get(`/checklist/${weddingId}`);
export const addChecklistItem = (weddingId, data) => api.post(`/checklist/${weddingId}`, data);
export const toggleChecklistItem = (id) => api.patch(`/checklist/${id}/toggle`);
export const deleteChecklistItem = (id) => api.delete(`/checklist/${id}`);

// Guests
export const getGuests = (weddingId, params) => api.get(`/guests/${weddingId}`, { params });
export const addGuest = (weddingId, data) => api.post(`/guests/${weddingId}`, data);
export const updateGuestRsvp = (id, data) => api.patch(`/guests/${id}/rsvp`, data);
export const deleteGuest = (id) => api.delete(`/guests/${id}`);

// Public RSVP (no auth needed)
export const submitPublicRsvp = (data) => api.post('/guests/rsvp/public', data);

// Delete wedding (soft delete)
export const deleteWedding = (id) => api.delete(`/weddings/${id}`);

// Admin: Restore deleted wedding
export const restoreWedding = (id) => api.patch(`/admin/weddings/${id}/restore`);

// Admin: Suspend/unsuspend/delete wedding
export const suspendWedding = (id, data) => api.patch(`/admin/weddings/${id}/suspend`, data);
export const unsuspendWedding = (id) => api.patch(`/admin/weddings/${id}/unsuspend`);
export const adminDeleteWedding = (id) => api.delete(`/admin/weddings/${id}`);

// Admin: Wedding suspension inquiry (public)
export const submitWeddingSuspensionInquiry = (data) => api.post('/auth/wedding-suspension-inquiry', data);

// Budget
export const getBudget = (weddingId) => api.get(`/budget/${weddingId}`);
export const addBudgetItem = (weddingId, data) => api.post(`/budget/${weddingId}`, data);
export const updateBudgetItem = (id, data) => api.put(`/budget/${id}`, data);
export const deleteBudgetItem = (id) => api.delete(`/budget/${id}`);
export const exportBudgetExcel = (weddingId) => api.get(`/budget/${weddingId}/export`, { responseType: 'blob' });

// Reviews
export const getVendorReviews = (vendorId) => api.get(`/reviews/vendor/${vendorId}`);
export const createReview = (data) => api.post('/reviews', data);

// Wedding Website
export const getWeddingWebsite = (subdomain) => api.get(`/weddings/website/${subdomain}`);
export const getMyWeddingWebsite = () => api.get('/weddings/my/website');
export const updateMyWeddingWebsite = (data) => api.put('/weddings/my/website', data);

// Admin
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminVendors = (params) => api.get('/admin/vendors', { params });
export const getAdminVendorDetail = (id) => api.get(`/admin/vendors/${id}`);
export const getVendorVerification = (params) => api.get('/admin/vendor-verification', { params });
export const approveVendor = (id) => api.patch(`/admin/vendors/${id}/approve`);
export const rejectVendor = (id) => api.patch(`/admin/vendors/${id}/reject`);
export const suspendVendor = (id) => api.patch(`/admin/vendors/${id}/suspend`);
export const unsuspendVendor = (id) => api.patch(`/admin/vendors/${id}/unsuspend`);
export const resetVendorPassword = (id) => api.post(`/admin/vendors/${id}/reset-password`);
export const verifyVendor = (id) => api.patch(`/admin/vendors/${id}/verify`);
export const unverifyVendor = (id) => api.patch(`/admin/vendors/${id}/unverify`);
export const getAdminUsers = (params) => api.get('/admin/users', { params });
export const getAdminCouples = () => api.get('/admin/couples');
export const getAdminCoupleDetail = (id) => api.get(`/admin/couples/${id}`);
export const getAdminWeddings = () => api.get('/admin/weddings');
export const getAdminReviews = () => api.get('/admin/reviews');
export const getAdminInquiries = () => api.get('/admin/inquiries');
export const markAdminInquiryRead = (id) => api.patch(`/admin/inquiries/${id}/read`);
export const markAdminInquiriesReadAll = () => api.patch('/admin/inquiries/mark-all-read');
export const getAdminActivity = () => api.get('/admin/activity');
export const testAdminEmail = (to) => api.post('/admin/test-email', { to });
export const submitSuspensionInquiry = (data) => api.post('/auth/suspension-inquiry', data);

// App Settings
export const getAppSettings = () => api.get('/settings/public');
export const getAdminSettings = () => api.get('/admin/settings');
export const updateAdminSettings = (data) => api.put('/admin/settings', data);

// Hero Images (public)
export const getHeroImages = () => api.get('/hero-images');

// Videos
export const getWeddingVideos = () => api.get('/videos/wedding');

// Hero Images (admin)
export const getAdminHeroImages = () => api.get('/admin/hero-images');
export const addHeroImage = (formData) => api.post('/admin/hero-images', formData);
export const updateHeroImage = (id, formData) => api.put(`/admin/hero-images/${id}`, formData);
export const deleteHeroImage = (id) => api.delete(`/admin/hero-images/${id}`);

// Vendor Portal
export const getVendorPortal = () => api.get('/vendors/my/portal');
export const updateVendorProfile = (data) => api.put('/vendors/my/profile', data);
export const uploadVendorCoverPhoto = (formData) => api.put('/vendors/my/cover-photo', formData);
export const uploadVendorPhotos = (formData) => api.post('/vendors/my/photos', formData);
export const addVendorVideo = (data) => api.post('/vendors/my/videos', data);
export const deleteVendorPhoto = (photoId) => api.delete(`/vendors/my/photos/${photoId}`);
export const replyToInquiry = (id, reply, attachmentFile) => {
  if (attachmentFile) {
    const formData = new FormData();
    formData.append('reply', reply);
    formData.append('attachment', attachmentFile);
    return api.put(`/vendors/my/inquiries/${id}/reply`, formData);
  }
  return api.put(`/vendors/my/inquiries/${id}/reply`, { reply });
};
export const updateInquiryStatus = (id, status) => api.put(`/vendors/my/inquiries/${id}/status`, { status });

// Google Vendors
export const getGoogleVendorStatus = () => api.get('/google-vendors/status');
export const getVendorSources = () => api.get('/google-vendors/sources');
export const syncGoogleVendors = (data) => api.post('/google-vendors/sync', data);
export const syncAllGoogleVendors = () => api.post('/google-vendors/sync-all');
export const refreshGoogleRatings = () => api.post('/google-vendors/refresh-ratings');
export const autoSyncGoogleVendors = (category, city) => api.post('/google-vendors/auto-sync', { category, city });
export const liveSearchGoogleVendors = (category, city) => api.get('/google-vendors/live-search', { params: { category, city } });
export const importGooglePlace = (google_place_id, category_slug) => api.post('/google-vendors/import-place', { google_place_id, category_slug });

// Collaborators (Helpers & Delegates)
export const getCollaborators = (weddingId) => api.get(`/collaborators/${weddingId}`);
export const addHelper = (weddingId, data) => api.post(`/collaborators/${weddingId}/helper`, data);
export const addDelegate = (weddingId, data) => api.post(`/collaborators/${weddingId}/delegate`, data);
export const acceptInvite = (token) => api.post(`/collaborators/accept/${token}`);
export const getInviteInfo = (token) => api.get(`/collaborators/invite-info/${token}`);
export const updateCollaborator = (id, data) => api.patch(`/collaborators/${id}`, data);
export const removeCollaborator = (id) => api.delete(`/collaborators/${id}`);
export const getMyCollaborations = () => api.get('/collaborators/my/weddings');
export const promoteToDelegate = (id, data) => api.patch(`/collaborators/${id}/promote`, data);

// Invite Codes
export const generateHelperCode = (weddingId) => api.post(`/weddings/${weddingId}/generate-helper-code`);
export const generateDelegateCode = (weddingId) => api.post(`/weddings/${weddingId}/generate-delegate-code`);
export const getInviteCodes = (weddingId) => api.get(`/weddings/${weddingId}/invite-codes`);
export const validateInviteCode = (code) => api.post('/weddings/validate-code', { code });

// Wedding Media
export const getWeddingMedia = (weddingId) => api.get(`/weddings/${weddingId}/media`);
export const uploadWeddingMedia = (weddingId, formData) => api.post(`/weddings/${weddingId}/media`, formData);
export const deleteWeddingMedia = (weddingId, mediaId) => api.delete(`/weddings/${weddingId}/media/${mediaId}`);

// Checklist assignments
export const assignChecklistItem = (id, data) => api.patch(`/checklist/${id}/assign`, data);

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const markNotificationRead = (id) => api.patch(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.patch('/notifications/read-all');
export const getNotificationPreferences = (weddingId) => api.get(`/notifications/preferences/${weddingId}`);
export const updateNotificationPreferences = (weddingId, data) => api.put(`/notifications/preferences/${weddingId}`, data);
export const sendReminders = () => api.post('/notifications/send-reminders');
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);

// Category Thumbnails (admin)
export const updateCategoryThumbnail = (id, formData) => api.put(`/categories/${id}/thumbnail`, formData);
export const getAdminCategories = () => api.get('/admin/categories');

// Family Members
export const getFamilyMembers = (weddingId) => api.get(`/weddings/${weddingId}/family`);
export const addFamilyMember = (weddingId, formData) => api.post(`/weddings/${weddingId}/family`, formData);
export const updateFamilyMember = (weddingId, memberId, formData) => api.put(`/weddings/${weddingId}/family/${memberId}`, formData);
export const deleteFamilyMember = (weddingId, memberId) => api.delete(`/weddings/${weddingId}/family/${memberId}`);
export const requestFamilyLimitIncrease = (weddingId, data) => api.post(`/weddings/${weddingId}/family/request-increase`, data);

// Bulk Vendor Actions
export const bulkVendorAction = (data) => api.post('/admin/vendors/bulk-action', data);

// Vendor Email Leads + Newsletter
export const getVendorLeads = (params) => api.get('/admin/vendor-leads', { params });
export const sendNewsletterEmails = (data) => api.post('/admin/vendor-leads/send-newsletter', data);

// Family Photo Limit Requests (admin)
export const getAdminFamilyRequests = () => api.get('/admin/family-requests');
export const approveFamilyRequest = (id) => api.patch(`/admin/family-requests/${id}/approve`);
export const rejectFamilyRequest = (id, data) => api.patch(`/admin/family-requests/${id}/reject`, data);

// Bookmarks
export const getBookmarks = () => api.get('/bookmarks');
export const bookmarkVendor = (vendorId) => api.post(`/bookmarks/${vendorId}`);
export const removeBookmark = (vendorId) => api.delete(`/bookmarks/${vendorId}`);
export const checkBookmark = (vendorId) => api.get(`/bookmarks/check/${vendorId}`);

// Admin: Vendor Bidding Requests
export const getAdminBiddingRequests = () => api.get('/admin/bidding-requests');
export const approveBiddingRequest = (vendorId) => api.patch(`/admin/vendors/${vendorId}/approve-bidding`);
export const denyBiddingRequest = (vendorId) => api.patch(`/admin/vendors/${vendorId}/deny-bidding`);
export const revokeBiddingAccess = (vendorId) => api.patch(`/admin/vendors/${vendorId}/revoke-bidding`);
export const adminDeleteVendor = (id) => api.delete(`/admin/vendors/${id}`);
export const adminDeleteUser = (id) => api.delete(`/admin/users/${id}`);
export const adminDeleteCouple = (id) => api.delete(`/admin/couples/${id}`);

// Couple Inquiries (view replies from vendors)
export const getMyInquiries = () => api.get('/inquiries/my');
export const getMyBookings = () => api.get('/inquiries/my-bookings');
export const getMyUnreadCount = () => api.get('/inquiries/my/unread-count');
export const getVendorUnreadCount = () => api.get('/inquiries/vendor/unread-count');
export const markInquiryCoupleRead = (id) => api.patch(`/inquiries/${id}/couple-read`);
export const getVendorInquiries = () => api.get('/inquiries/vendor/all');
export const markVendorInquiryRead = (id) => api.patch(`/inquiries/${id}/vendor-read`);
export const markVendorInquiriesReadAll = () => api.patch('/inquiries/vendor/mark-all-read');

// Bidding System
export const requestBiddingAccess = () => api.post('/inquiries/request-bidding');
export const getBidListings = (params) => api.get('/inquiries/bid-listings', { params });
export const getBidListing = (id) => api.get(`/inquiries/bid-listings/${id}`);
export const createBidListing = (data) => api.post('/inquiries/bid-listings', data);
export const updateBidListing = (id, data) => api.put(`/inquiries/bid-listings/${id}`, data);
export const deleteBidListing = (id) => api.delete(`/inquiries/bid-listings/${id}`);
export const getMyBidListings = () => api.get('/inquiries/my-listings');
export const placeBid = (listingId, data) => api.post(`/inquiries/bids/${listingId}`, data);
export const updateBidStatus = (id, data) => api.patch(`/inquiries/bids/${id}/status`, data);
export const withdrawBid = (id) => api.patch(`/inquiries/bids/${id}/withdraw`);
export const getMyBids = () => api.get('/inquiries/my-bids');
export const blockBidder = (data) => api.post('/inquiries/block-bidder', data);
export const unblockBidder = (userId) => api.delete(`/inquiries/block-bidder/${userId}`);
export const sendBidInquiry = (listingId, data) => api.post(`/inquiries/bid-inquiry/${listingId}`, data);
export const changePassword = (data) => api.post('/auth/change-password', data);
export const getMyVendorInquiry = (vendorId) => api.get(`/inquiries/vendor/${vendorId}/mine`);

// Conversation Messages
export const getChatPreview = () => api.get('/inquiries/chat-preview');
export const getInquiryMessages = (inquiryId) => api.get(`/inquiries/${inquiryId}/messages`);
export const sendInquiryMessage = (inquiryId, message, attachmentFile) => {
  if (attachmentFile) {
    const formData = new FormData();
    formData.append('message', message);
    formData.append('attachment', attachmentFile);
    return api.post(`/inquiries/${inquiryId}/messages`, formData);
  }
  return api.post(`/inquiries/${inquiryId}/messages`, { message });
};

// Admin mailbox (inbound/outbound email log)
export const getEmailAdminStats = () => api.get('/email-admin/stats');

// SES Mailbox
export const getSESMetrics = () => api.get('/ses/metrics');
export const getSESEmails = (params) => api.get('/ses/emails', { params });
export const sendSESEmail = (data) => api.post('/ses/send', data);
export const replySESEmail = (id, data) => api.post(`/ses/reply/${id}`, data);
export const deleteSESEmail = (id) => api.delete(`/ses/emails/${id}`);
export const toggleSESSpam = (id, is_spam) => api.patch(`/ses/emails/${id}/spam`, { is_spam });
export const bulkDeleteSESEmails = (ids) => api.post('/ses/bulk-delete', { ids });
export const getSESIdentities = () => api.get('/ses/identities');

// Analytics
export const trackPageView = (path, referrer) => api.post('/analytics/pageview', { path, referrer }).catch(() => {});
export const trackVendorClick = (vendor_id, click_type, referrer) => api.post('/analytics/vendor-click', { vendor_id, click_type, referrer }).catch(() => {});
export const getAnalyticsDashboard = (days) => api.get('/analytics/dashboard', { params: { days } });

// Wedding Invitations
export const getInvitations = (weddingId) => api.get(`/invitations/${weddingId}`);
export const getInvitationStats = (weddingId) => api.get(`/invitations/${weddingId}/stats`);
export const createInvitation = (weddingId, data) => api.post(`/invitations/${weddingId}`, data);
export const createBulkInvitations = (weddingId, data) => api.post(`/invitations/${weddingId}/bulk`, data);
export const sendInvitation = (id) => api.post(`/invitations/send/${id}`);
export const sendBulkInvitations = (weddingId) => api.post(`/invitations/send-bulk/${weddingId}`);
export const resendInvitation = (id) => api.post(`/invitations/resend/${id}`);
export const updateInvitation = (id, data) => api.put(`/invitations/${id}`, data);
export const deleteInvitation = (id) => api.delete(`/invitations/${id}`);
export const getRsvpDetails = (token) => api.get(`/invitations/rsvp/${token}`);
export const submitRsvp = (token, data) => api.post(`/invitations/rsvp/${token}`, data);
export const getAdminInvitations = () => api.get('/invitations/admin/all');
export const updateReminderFrequency = (id, reminder_frequency) => api.put(`/invitations/${id}/reminder`, { reminder_frequency });
export const updateBulkReminderFrequency = (weddingId, reminder_frequency) => api.put(`/invitations/${weddingId}/reminder-bulk`, { reminder_frequency });
export const uploadCardPhoto = (file) => {
  const formData = new FormData();
  formData.append('photo', file);
  return api.post('/invitations/upload-card-photo', formData);
};

export const uploadInvitationAudio = (weddingId, file) => {
  const formData = new FormData();
  formData.append('audio', file);
  return api.post(`/invitations/upload-audio/${weddingId}`, formData);
};

export const removeInvitationAudio = (weddingId) => api.delete(`/invitations/audio/${weddingId}`);

// Admin: release notes / changelog
export const getAdminReleases = () => api.get('/admin/releases');

// Dashboard background image (couple)
export const uploadDashboardBg = (weddingId, formData) => api.patch(`/weddings/${weddingId}/dashboard-bg`, formData);
export const removeDashboardBg = (weddingId) => api.delete(`/weddings/${weddingId}/dashboard-bg`);
export const uploadWeddingWebsiteBg = (formData) => api.patch('/weddings/my/website/bg', formData);
export const updateWeddingCurrency = (weddingId, currency) => api.patch(`/weddings/${weddingId}/currency`, { budget_currency: currency });

// Admin: default dashboard background
export const uploadAdminDashboardBg = (formData) => api.post('/admin/settings/dashboard-bg', formData);

// Vendor Team
export const getVendorTeamInviteInfo = (token) => api.get(`/vendor-team/invite-info/${token}`);
export const inviteEmployee = (data) => api.post('/vendor-team/employees/invite', data);
export const resendEmployeeInvite = (id) => api.post(`/vendor-team/employees/${id}/resend-invite`);
export const preAcceptEmployeeInvite = (token) => api.post(`/vendor-team/pre-accept/${token}`);
export const getEmployees = () => api.get('/vendor-team/employees');
export const updateEmployee = (id, data) => api.patch(`/vendor-team/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/vendor-team/employees/${id}`);
export const cancelEmployeeInvite = (id) => api.post(`/vendor-team/employees/${id}/cancel-invite`);
export const getVendorProjects = () => api.get('/vendor-team/projects');
export const createVendorProject = (data) => api.post('/vendor-team/projects', data);
export const getVendorProject = (id) => api.get(`/vendor-team/projects/${id}`);
export const updateVendorProject = (id, data) => api.patch(`/vendor-team/projects/${id}`, data);
export const deleteVendorProject = (id) => api.delete(`/vendor-team/projects/${id}`);
export const cancelVendorProject = (id, reason) => api.post(`/vendor-team/projects/${id}/cancel`, { reason });
export const assignEmployee = (projectId, data) => api.post(`/vendor-team/projects/${projectId}/assign`, data);
export const unassignEmployee = (projectId, employeeId) => api.delete(`/vendor-team/projects/${projectId}/assign/${employeeId}`);
export const reassignEmployee = (projectId, data) => api.post(`/vendor-team/projects/${projectId}/reassign`, data);
export const getMyProjects = () => api.get('/vendor-team/my-projects');
export const acceptEmployeeInvite = (token) => api.post(`/vendor-team/accept/${token}`);
export const getEmployeeProfile = (id) => api.get(`/vendor-team/employees/${id}/profile`);
export const updateEmployeeProfile = (id, data) => api.patch(`/vendor-team/employees/${id}/profile`, data);
export const getMyProfile = () => api.get('/vendor-team/my-profile');
export const updateMyProfile = (data) => api.patch('/vendor-team/my-profile', data);
export const getProjectHistory = (id) => api.get(`/vendor-team/projects/${id}/history`);
export const sendProjectReminder = (id) => api.post(`/vendor-team/projects/${id}/reminders/send`);
export const getProjectReminders = (id) => api.get(`/vendor-team/projects/${id}/reminders`);

// Reviews
export const getMyReviewInfo = () => api.get('/reviews/my-review-info');
export const submitWeddingReview = (data) => api.post('/reviews/wedding', data);

// Admin — Completed Weddings
export const getAdminCompletedWeddings = () => api.get('/admin/completed-weddings');
export const adminMarkWeddingComplete = (id, data = {}) => api.post(`/admin/completed-weddings/${id}/mark-complete`, data);

// Admin — Studio Contracts
export const getContracts = (params) => api.get('/admin/contracts', { params });
export const getContract = (id) => api.get(`/admin/contracts/${id}`);
export const createContract = (data) => api.post('/admin/contracts', data);
export const updateContract = (id, data) => api.put(`/admin/contracts/${id}`, data);
export const deleteContract = (id) => api.delete(`/admin/contracts/${id}`);
export const sendContract = (id) => api.post(`/admin/contracts/${id}/send`);
export const remindContract = (id) => api.post(`/admin/contracts/${id}/remind`);
export const duplicateContract = (id) => api.post(`/admin/contracts/${id}/duplicate`);
export const countersignContract = (id, signature) => api.post(`/admin/contracts/${id}/countersign`, { signature });
export const cancelContract = (id) => api.post(`/admin/contracts/${id}/cancel`);
export const downloadContractDocument = (id, format = 'pdf') => api.get(`/admin/contracts/${id}/document`, { params: { format }, responseType: 'blob' });
export const getContractTemplates = () => api.get('/admin/contracts/templates');
export const createContractTemplate = (data) => api.post('/admin/contracts/templates', data);
export const updateContractTemplate = (id, data) => api.put(`/admin/contracts/templates/${id}`, data);
export const deleteContractTemplate = (id) => api.delete(`/admin/contracts/templates/${id}`);

// Public — client signs a contract via emailed link
export const getContractForSigning = (token) => api.get(`/contracts/sign/${token}`);
export const signContract = (token, data) => api.post(`/contracts/sign/${token}`, data);
// The signing token is the only credential, so this can be a plain link.
export const contractDocumentUrl = (token, format = 'pdf') => `${API_URL}/contracts/sign/${token}/document?format=${format}`;

export default api;
